import { clsx } from "clsx";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { useEffect, useId, useMemo, useRef, useState } from "react";

const COUNTRY_CODES = getCountries();
const COUNTRY_CODE_SET = new Set<string>(COUNTRY_CODES);

export type PhoneNumberInputProps = {
  defaultCountry?: string;
  locale?: string;
  label?: string;
  hideLabel?: boolean;
  required?: boolean;
  value: string;
  onChange: (phoneNumber: string) => void;
  error?: string;
  disabled?: boolean;
  savedInput?: string;
  savedCountry?: string;
  onInputChange?: (input: string, country: string) => void;
};

type CountryOption = {
  code: CountryCode;
  name: string;
  callingCode: string;
};

function countryFlag(countryCode: CountryCode): string {
  return String.fromCodePoint(
    ...countryCode
      .toUpperCase()
      .split("")
      .map((character) => 127397 + character.charCodeAt(0)),
  );
}

function supportedCountry(
  countryCode: string | undefined,
): CountryCode | undefined {
  const normalized = countryCode?.toUpperCase();
  return normalized && COUNTRY_CODE_SET.has(normalized)
    ? (normalized as CountryCode)
    : undefined;
}

function countryFromTimeZone(timeZone: string): CountryCode | undefined {
  const matches = COUNTRY_CODES.filter((code) => {
    const locale = new Intl.Locale(`und-${code}`) as Intl.Locale & {
      getTimeZones?: () => string[];
      timeZones?: string[];
    };
    return (locale.getTimeZones?.() ?? locale.timeZones)?.includes(timeZone);
  });
  // Older browsers may not expose timezone-region data; use language below instead.
  return matches.length === 1 ? matches[0] : undefined;
}

function countryFromLanguages(
  languages: readonly string[],
): string | undefined {
  for (const language of languages) {
    try {
      const region = new Intl.Locale(language).region;
      if (region && supportedCountry(region)) return region;
    } catch {
      // Ignore malformed browser locales and try the next preference.
    }
  }
  return undefined;
}

function getInitialValue(value: string, fallbackCountry: CountryCode) {
  const parsed = parsePhoneNumberFromString(value);
  return {
    country: parsed?.country ?? fallbackCountry,
    displayValue: parsed?.formatNational() ?? "",
  };
}

function parseNationalNumber(value: string, country: CountryCode): string {
  const parsed = parsePhoneNumberFromString(value, country);
  return parsed?.number ?? "";
}

export function PhoneNumberInput({
  value,
  onChange,
  error,
  disabled,
  savedInput,
  savedCountry,
  onInputChange,
  defaultCountry,
  locale = "en-AU",
  label = "Phone number",
  hideLabel = false,
  required,
}: PhoneNumberInputProps) {
  const id = useId();
  const messageId = useId();
  const fallbackCountry = supportedCountry(defaultCountry) ?? "AU";
  const initialValue = getInitialValue(value, fallbackCountry);
  const [country, setCountry] = useState<CountryCode>(
    supportedCountry(savedCountry) ?? initialValue.country,
  );
  const [displayValue, setDisplayValue] = useState(
    savedInput || initialValue.displayValue,
  );
  const hasInteracted = useRef(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
    if (hasInteracted.current || value || savedCountry) return;
    setCountry(
      supportedCountry(defaultCountry) ??
        countryFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ??
        supportedCountry(countryFromLanguages(navigator.languages)) ??
        "AU",
    );
  }, [defaultCountry, value, savedCountry]);

  const countries = useMemo<CountryOption[]>(() => {
    let names: Intl.DisplayNames | undefined;
    // Intl region names vary between Node and browser ICU versions. Keep the
    // initial options deterministic, then localise them after hydration.
    if (hydrated) {
      try {
        names = new Intl.DisplayNames([locale], { type: "region" });
      } catch {
        names = undefined;
      }
    }

    const priority: Partial<Record<CountryCode, number>> = { AU: 0, US: 1 };
    return COUNTRY_CODES.map((code) => ({
      code,
      name: names?.of(code) ?? code,
      callingCode: getCountryCallingCode(code),
    })).sort(
      (first, second) =>
        (priority[first.code] ?? 2) - (priority[second.code] ?? 2) ||
        (names
          ? first.name.localeCompare(second.name, locale)
          : first.code.localeCompare(second.code, "en")),
    );
  }, [locale, hydrated]);

  const placeholder =
    getExampleNumber(country, examples)?.formatNational() ?? label;

  const updatePhoneNumber = (nextValue: string, nextCountry: CountryCode) => {
    hasInteracted.current = true;
    if (nextValue.trim().startsWith("+")) {
      const internationalNumber = parsePhoneNumberFromString(nextValue);
      if (internationalNumber?.country) {
        setCountry(internationalNumber.country);
        setDisplayValue(internationalNumber.formatNational());
        onInputChange?.(
          internationalNumber.formatNational(),
          internationalNumber.country,
        );
        onChange(internationalNumber.number);
        return;
      }
    }

    const digits = nextValue.replace(/\D/g, "");
    const formatted = digits ? new AsYouType(nextCountry).input(digits) : "";
    setDisplayValue(formatted);
    onInputChange?.(formatted, nextCountry);
    onChange(parseNationalNumber(formatted, nextCountry));
  };

  return (
    <div>
      <label
        htmlFor={id}
        className={
          hideLabel
            ? "sr-only"
            : "mb-2 block font-sans text-sm text-butter-green"
        }
      >
        {label}
      </label>
      <div
        className={clsx(
          "flex h-14 rounded-[9px] border bg-white transition-colors",
          error
            ? "border-butter-pink focus-within:border-butter-pink"
            : "border-butter-cream focus-within:border-butter-green/60",
        )}
      >
        <div className="relative flex shrink-0 rounded-l-[8px] border-r border-inherit text-butter-green">
          <select
            disabled={disabled}
            aria-label="Country or region"
            autoComplete="tel-country-code"
            value={country}
            onChange={(event) => {
              const nextCountry = event.target.value as CountryCode;
              setCountry(nextCountry);
              updatePhoneNumber(displayValue, nextCountry);
            }}
            className="absolute inset-0 size-full cursor-pointer appearance-none font-sans text-base opacity-0"
          >
            {countries.map((option) => (
              <option key={option.code} value={option.code}>
                {option.name} (+{option.callingCode})
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none flex items-center gap-1.5 px-3"
          >
            <span className="text-lg">{countryFlag(country)}</span>
            <span className="font-sans text-base font-normal">
              +{getCountryCallingCode(country)}
            </span>
            <svg
              aria-hidden="true"
              className="size-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                d="m6 9 6 6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <input
          id={id}
          required={required}
          disabled={disabled}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={displayValue}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? messageId : undefined}
          onChange={(event) => updatePhoneNumber(event.target.value, country)}
          className="min-w-0 flex-1 bg-transparent px-3 font-sans text-base text-butter-green outline-none placeholder:text-butter-green/50"
        />
      </div>
      {error && (
        <p
          id={messageId}
          role="alert"
          className="mt-2 font-sans text-sm leading-[1.4] text-butter-pink"
        >
          {error}
        </p>
      )}
    </div>
  );
}
